import type { FormItem, Section } from '../../../../../api/types/expenseareas';

const flattenServerErrorsToPaths = (
    raw: unknown,
    parts: string[] = [],
    out: Record<string, string[]> = {}
): Record<string, string[]> => {
    if (raw == null) return out;

    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
        const path = parts.join('.');
        if (!path) return out;
        if (!out[path]) out[path] = [];
        out[path].push(String(raw));
        return out;
    }

    if (Array.isArray(raw)) {
        if (raw.length === 0) return out;
        if (raw.every((x) => x == null || ['string', 'number', 'boolean'].includes(typeof x))) {
            const path = parts.join('.');
            if (!path) return out;
            if (!out[path]) out[path] = [];
            for (const x of raw) out[path].push(String(x));
            return out;
        }
        raw.forEach((item, i) => flattenServerErrorsToPaths(item, [...parts, String(i)], out));
        return out;
    }

    if (typeof raw === 'object') {
        for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
            flattenServerErrorsToPaths(v, [...parts, k], out);
        }
    }
    return out;
};

const normalizeErrorsPayload = (raw: unknown): unknown => {
    if (!Array.isArray(raw)) return raw;

    const rows = raw.filter((x) => x && typeof x === 'object') as Record<string, unknown>[];
    if (
        rows.length > 0 &&
        rows.every((x) => {
            const key = x.field ?? x.key ?? x.attribute;
            return typeof key === 'string' && (x.message != null || x.messages != null || x.msg != null);
        })
    ) {
        const o: Record<string, string[]> = {};
        for (const x of rows) {
            const path = String(x.field ?? x.key ?? x.attribute ?? '');
            if (!path) continue;
            const msg = x.message ?? x.messages ?? x.msg;
            const list = Array.isArray(msg) ? msg.map(String) : [String(msg)];
            if (!o[path]) o[path] = [];
            o[path].push(...list.filter((s) => s && s.trim() !== ''));
        }
        return o;
    }

    return raw;
};

const collectMsgs = (rawMsgs: unknown): string[] => {
    if (Array.isArray(rawMsgs)) {
        return rawMsgs.map(String).map((s) => s.trim()).filter(Boolean);
    }
    if (rawMsgs == null) return [];
    const s = String(rawMsgs).trim();
    return s ? [s] : [];
};

/** Сообщения 422 для publish/draft форм: поля и секции по индексам в теле запроса. */
export function applyServerErrorsToFormSections(
    sections: Section[],
    rawErrors: unknown
): { nextSections: Section[]; unmapped: string[] } {
    const cleared = sections.map((s) => ({
        ...s,
        serverErrorMessage: undefined as string | undefined,
        fields: s.fields.map((f) => ({ ...f, serverErrorMessage: undefined as string | undefined }))
    }));

    if (rawErrors == null) {
        return { nextSections: cleared, unmapped: [] };
    }

    const normalized = normalizeErrorsPayload(rawErrors);
    const flat = flattenServerErrorsToPaths(normalized);
    const byFieldId = new Map<string, string[]>();
    const sectionMessagesByIndex = new Map<number, string[]>();

    const unmapped: string[] = [];

    const fieldPathRe = /(?:^|\.)(?:data\.)?sections\.(\d+)\.fields\.(\d+)(?:\.|$)/;
    const sectionMetaRe = /(?:^|\.)(?:data\.)?sections\.(\d+)\.(title|description)(?:\.|$)/;

    for (const [path, rawList] of Object.entries(flat)) {
        const msgs = collectMsgs(rawList);
        if (!msgs.length) continue;

        const fm = path.match(fieldPathRe);
        if (fm) {
            const si = parseInt(fm[1], 10);
            const fi = parseInt(fm[2], 10);
            const field = cleared[si]?.fields?.[fi] as FormItem | undefined;
            if (field?.id != null) {
                const id = String(field.id);
                const prev = byFieldId.get(id) ?? [];
                byFieldId.set(id, [...prev, ...msgs]);
                continue;
            }
        }

        const sm = path.match(sectionMetaRe);
        if (sm) {
            const si = parseInt(sm[1], 10);
            const prev = sectionMessagesByIndex.get(si) ?? [];
            sectionMessagesByIndex.set(si, [...prev, ...msgs]);
            continue;
        }

        unmapped.push(...msgs);
    }

    const next = cleared.map((section, sIdx) => {
        const secMsgs = sectionMessagesByIndex.get(sIdx);
        const sectionMsg = secMsgs?.length ? [...new Set(secMsgs)].join('; ') : undefined;

        return {
            ...section,
            ...(sectionMsg ? { serverErrorMessage: sectionMsg } : {}),
            fields: section.fields.map((f) => {
                const list = byFieldId.get(String(f.id));
                if (!list?.length) return f;
                const text = [...new Set(list)].join('; ');
                return { ...f, serverErrorMessage: text };
            })
        };
    });

    return { nextSections: next, unmapped: [...new Set(unmapped)] };
}
